
    import { Component, Vue, Prop, Emit } from 'vue-property-decorator';

    // 适配后端返回的装箱详情类型（小写字段，与你后端PackingBatchDetailDto一致）
    interface BackendPackingDetail {
        id: number;
        packingPlanId: number;
        boxNumber: number;
        productId: number;
        productNums: number;
        creationTime: Date | string;
        userId: number;
    }

    // 前端装箱详情列表项类型
    interface PackingDetailItem {
        Id: number;
        PackingPlanId: number;
        BoxNumber: number;
        ProductId: number;
        Quantity: number;
        CreationTime: string;
        UserId: number;
        uniqueKey: string;
    }

    // 顶部输入表单类型
    interface AddForm {
        BoxNumber: number | '';
        ProductId: number | '';
        Quantity: number | '';
        CreationTime: string | null;
    }

    @Component({ name: 'PackingDetailForm' })
    export default class PackingDetailForm extends Vue {
        // 弹窗显隐状态
        @Prop({ type: Boolean, default: false }) value!: boolean;
        // 装箱计划ID（你现有父组件传递的参数：row.id）
        @Prop({ type: Number, default: 0 }) packingPlanId!: number;
        // 初始化详情列表（你现有父组件传递的：row.packingDetailList）
        @Prop({ type: Array, default: () => [] }) initDetailList!: BackendPackingDetail[];
        @Prop({ type: Array, default: () => [] }) productIds!: number[]; // 新增：接收产品内码列表

        // 前端装箱详情列表
        packingDetailList: PackingDetailItem[] = [];
        // 顶部添加表单
        form: AddForm = {
            BoxNumber: '',
            ProductId: '',
            Quantity: '',
            CreationTime: null
        };
        // 当前登录用户ID（可从store获取，保持原有默认）
        get currentUserId() {
            return 0;
        }
        // 选中的列表项（用于批量删除）
        selectedItems: PackingDetailItem[] = [];

        // 弹窗显隐变化时初始化
        visibleChange(val: boolean) {
            if (val) {
                this.initPackingDetails();
            } else {
                this.resetFormAndList();
            }
        }

        // 格式化时间：将后端DateTime转换为前端标准字符串
        formatDateTime(date: Date | string): string {
            if (!date) return this.getCurrentFormatTime();
            const targetDate = typeof date === 'string' ? new Date(date) : date;
            const year = targetDate.getFullYear();
            const month = (targetDate.getMonth() + 1).toString().padStart(2, '0');
            const day = targetDate.getDate().toString().padStart(2, '0');
            const hours = targetDate.getHours().toString().padStart(2, '0');
            const minutes = targetDate.getMinutes().toString().padStart(2, '0');
            const seconds = targetDate.getSeconds().toString().padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }

        // 格式化当前时间为标准格式
        getCurrentFormatTime() {
            const now = new Date();
            const year = now.getFullYear();
            const month = (now.getMonth() + 1).toString().padStart(2, '0');
            const day = now.getDate().toString().padStart(2, '0');
            const hours = now.getHours().toString().padStart(2, '0');
            const minutes = now.getMinutes().toString().padStart(2, '0');
            const seconds = now.getSeconds().toString().padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }

        // 初始化装箱详情（修正PackingPlanId赋值，适配后端小写字段）
        initPackingDetails() {
            if (!this.initDetailList || this.initDetailList.length === 0) {
                this.packingDetailList = [];
                return;
            }

            // 深拷贝+字段映射（后端小写字段→前端大写字段）+ 赋值有效PackingPlanId
            this.packingDetailList = JSON.parse(JSON.stringify(this.initDetailList))
                .map((item: BackendPackingDetail) => ({
                    Id: item.id,
                    // 优先取后端详情中的packingPlanId，无则取父组件传递的packingPlanId（row.id）
                    PackingPlanId: item.packingPlanId || this.packingPlanId,
                    BoxNumber: item.boxNumber,
                    ProductId: item.productId,
                    Quantity: item.productNums,
                    CreationTime: this.formatDateTime(item.creationTime),
                    UserId: Number(item.userId) || this.currentUserId,
                    uniqueKey: `item_${item.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                }))
                .sort((a, b) => a.BoxNumber - b.BoxNumber);
        }

        // 重置表单和列表
        resetFormAndList() {
            this.form = {
                BoxNumber: '',
                ProductId: '',
                Quantity: '',
                CreationTime: null
            };
            this.packingDetailList = [];
            this.selectedItems = [];
            const packingTable = this.$refs.packingTable;
            if (packingTable) {
                (packingTable as any).clearSelection();
            }
        }

        // 仅提交时校验箱号连续性（允许重复递增，禁止跳级）
        checkBoxNumberContinuous(): boolean {
            if (this.packingDetailList.length <= 1) return true;

            const uniqueBoxes = [...new Set(this.packingDetailList.map(item => item.BoxNumber))].sort((a, b) => a - b);
            for (let i = 1; i < uniqueBoxes.length; i++) {
                if (uniqueBoxes[i] - uniqueBoxes[i - 1] > 1) {
                    alert(`箱号不合法！存在跳级，缺少箱号 ${uniqueBoxes[i - 1] + 1}（当前箱号范围：${uniqueBoxes[0]} - ${uniqueBoxes[uniqueBoxes.length - 1]}）`);
                    return false;
                }
            }

            return true;
        }

        // 监听表格选中项变化
        handleSelectionChange(selectedItems: PackingDetailItem[]) {
            this.selectedItems = selectedItems;
        }

        // 添加列表项：仅校验必填项，无添加成功提示
        handleAddItem() {
            const { BoxNumber, ProductId, Quantity, CreationTime } = this.form;
            const inputProductId = Number(ProductId) || 0; // 转为数字类型
            // 1. 校验产品内码是否有效
            if (!ProductId || !this.productIds.includes(ProductId)) {
                alert(`请选择有效的产品内码（仅允许：${this.productIds.join(', ')}）`);
                return;
            }
            // 原有必填项校验
            if (!BoxNumber || BoxNumber <= 0) {
                alert('请输入有效的箱号（正整数）');
                return;
            }
            if (!Quantity || Quantity <= 0) {
                alert('请输入有效的装箱数量（正整数）');
                return;
            }

            const finalCreationTime = CreationTime || this.getCurrentFormatTime();

            // 构造新项：直接使用父组件传递的packingPlanId（row.id），确保非0
            const newItem: PackingDetailItem = {
                Id: 0,
                PackingPlanId: this.packingPlanId, // 关键：赋值父组件传递的有效packingPlanId
                BoxNumber,
                ProductId: inputProductId,
                Quantity,
                CreationTime: finalCreationTime,
                UserId: this.currentUserId,
                uniqueKey: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            };

            this.packingDetailList.push(newItem);
            this.packingDetailList = this.packingDetailList.sort((a, b) => a.BoxNumber - b.BoxNumber);

            // 重置表单，无任何添加提示
            this.form = {
                BoxNumber: '',
                ProductId: '',
                Quantity: '',
                CreationTime: null
            };
        }

        // 删除单条装箱详情
        deletePackingDetail(index: number) {
            if (index >= 0 && index < this.packingDetailList.length) {
                this.packingDetailList.splice(index, 1);
                this.packingDetailList = this.packingDetailList.sort((a, b) => a.BoxNumber - b.BoxNumber);
                this.$forceUpdate();
                alert('已删除该装箱详情');
            }
        }

        // 删除选中的装箱详情（按钮固定显示）
        deleteSelectedItems() {
            if (this.selectedItems.length === 0) {
                alert('请先选中要删除的项');
                return;
            }
            const selectedKeys = this.selectedItems.map(item => item.uniqueKey);
            this.packingDetailList = this.packingDetailList.filter(item => !selectedKeys.includes(item.uniqueKey));
            this.packingDetailList = this.packingDetailList.sort((a, b) => a.BoxNumber - b.BoxNumber);

            const packingTable = this.$refs.packingTable;
            if (packingTable) {
                (packingTable as any).clearSelection();
            }
            this.selectedItems = [];
            alert('已删除选中的装箱详情');
        }

        // 校验装箱详情整体有效性
        validatePackingDetails(): boolean {
            if (this.packingDetailList.length === 0) {
                alert('请至少添加一条装箱详情');
                return false;
            }

            for (const item of this.packingDetailList) {
                if (!item.BoxNumber || item.BoxNumber <= 0) {
                    alert(`产品内码${item.ProductId}：箱号无效`);
                    return false;
                }
                if (!item.ProductId || item.ProductId <= 0) {
                    alert(`第${item.BoxNumber}箱：产品内码无效`);
                    return false;
                }
                if (!item.Quantity || item.Quantity <= 0) {
                    alert(`第${item.BoxNumber}箱：装箱数量无效`);
                    return false;
                }
            }

            if (!this.checkBoxNumberContinuous()) {
                return false;
            }

            return true;
        }

        // 提交装箱详情：移除try/catch，直接调用接口（依赖后台统一异常弹窗）
        @Emit('save-success')
        async handleSubmit() {
            // 1. 校验（包含箱号连续性最终校验）
            if (!this.validatePackingDetails()) {
                return;
            }

            // 2. 构造提交参数：前端大写字段→后端小写字段，PackingPlanId为父组件传递的有效值
            const submitData = {
                userId: this.currentUserId,
                packingPlanId: this.packingPlanId, // 有效PackingPlanId（row.id）
                packingDetails: this.packingDetailList.map(item => ({
                    id: item.Id,
                    packingPlanId: item.PackingPlanId,
                    boxNumber: item.BoxNumber,
                    productId: item.ProductId,
                    productNums: item.Quantity,
                    creationTime: new Date(item.CreationTime),
                    userId: item.UserId
                }))
            };

            // 3. 直接调用接口，移除try/catch（后台统一处理异常弹窗）
            await this.$store.dispatch({
                type: 'frontPackingBatch/savePackingDetails',
                data: submitData
            });

            alert('装箱详情保存成功！');
            this.$emit('save-success');
            this.handleCancel();
        }

        // 取消弹窗
        handleCancel() {
            this.$emit('input', false);
            this.resetFormAndList();
        }

        // 表格列配置
        get detailColumns() {
            return [
                {
                    type: 'selection',
                    width: 60,
                    align: 'center'
                },
                {
                    title: '箱号',
                    key: 'BoxNumber',
                    width: 100,
                    align: 'center',
                    sortable: true
                },
                {
                    title: '产品内码',
                    key: 'ProductId',
                    width: 120,
                    align: 'center'
                },
                {
                    title: '装箱数量',
                    key: 'Quantity',
                    width: 120,
                    align: 'center'
                },
                {
                    title: '操作时间',
                    key: 'CreationTime',
                    width: 220,
                    align: 'center'
                },
                {
                    title: '操作',
                    key: 'operation',
                    width: 100,
                    align: 'center',
                    slot: 'operation'
                }
            ];
        }
    }
